import {
  FieldProps,
  FieldPathId,
  ErrorSchema,
  RJSFSchema,
  UiSchema,
} from "@rjsf/utils";
import { Grid, Typography } from "@mui/material";

// Type for the layout grid row structure
interface LayoutGridRow {
  "ui:row"?: {
    spacing?: number;
    className?: string;
    children?: LayoutGridCol[];
  };
}

interface LayoutGridCol {
  "ui:col"?: {
    size?: {
      xs?: number;
      sm?: number;
      md?: number;
      lg?: number;
      xl?: number;
    };
    children?: string[];
  };
}

// Helper to get a nested property using dot notation path
function getNestedValue<T>(
  obj: Record<string, unknown> | undefined,
  path: string
): T | undefined {
  if (!obj) return undefined;
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== "object"
    ) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current as T;
}

// Helper to get nested schema property using dot notation
function getNestedSchemaProperty(
  schema: RJSFSchema,
  path: string,
  schemaUtils: FieldProps["registry"]["schemaUtils"],
  formData: unknown
): RJSFSchema | undefined {
  const parts = path.split(".");
  let currentSchema = schema;

  for (const part of parts) {
    // Resolve the current schema first
    const resolved = schemaUtils.retrieveSchema(currentSchema, formData);

    if (!resolved.properties || !resolved.properties[part]) {
      return undefined;
    }

    const propSchema = resolved.properties[part];
    if (typeof propSchema === "boolean") {
      return undefined;
    }
    currentSchema = propSchema as RJSFSchema;
  }

  return currentSchema;
}

// Helper to check if a nested path exists in schema
function hasNestedSchemaProperty(
  schema: RJSFSchema,
  path: string,
  schemaUtils: FieldProps["registry"]["schemaUtils"],
  formData: unknown
): boolean {
  return (
    getNestedSchemaProperty(schema, path, schemaUtils, formData) !== undefined
  );
}

export const LayoutGridField = (props: FieldProps) => {
  const {
    uiSchema,
    registry,
    formData,
    fieldPathId,
    schema,
    errorSchema,
    onChange,
    onBlur,
    onFocus,
    disabled,
    readonly,
  } = props;
  const { SchemaField } = registry.fields;
  const layoutGrid = uiSchema?.["ui:layoutGrid"] as
    | { "ui:row"?: LayoutGridRow[] }
    | undefined;

  // Retrieve the fully resolved schema for checking properties
  const resolvedSchema = registry.schemaUtils.retrieveSchema(schema, formData);

  if (!layoutGrid || !layoutGrid["ui:row"]) {
    // Fallback to default object rendering if no layout grid
    const { ObjectField } = registry.fields;
    // Remove ui:field from uiSchema to prevent infinite recursion
    const { "ui:field": _, ...restUiSchema } = uiSchema || {};
    return <ObjectField {...props} uiSchema={restUiSchema} />;
  }

  // Helper to create a FieldPathId for a child field (RJSF v6 API)
  // Supports dot notation paths like "Person.FirstName"
  const getChildFieldPathId = (fieldPath: string): FieldPathId => {
    const parentId = fieldPathId?.$id || "root";
    const parentPath = fieldPathId?.path || [];
    const pathParts = fieldPath.split(".");
    return {
      $id: `${parentId}_${pathParts.join("_")}`,
      path: [...parentPath, ...pathParts],
    };
  };

  // Helper to handle child onChange for potentially nested fields
  const handleChildChange = (_fieldPath: string) => {
    return (
      value: unknown,
      path: (string | number)[],
      es?: ErrorSchema<unknown>,
      id?: string
    ) => {
      onChange(value, path, es as ErrorSchema<unknown>, id);
    };
  };

  // If no rows defined, fall back to ObjectField
  if (layoutGrid["ui:row"].length === 0) {
    const { ObjectField } = registry.fields;
    const { "ui:field": _, ...restUiSchema } = uiSchema || {};
    return <ObjectField {...props} uiSchema={restUiSchema} />;
  }

  // At the root form, the layout grid begins with the section-less scalar fields
  // (City, ReportedDate, etc.) before any array sections. Group those visually under
  // a "General Information" heading so the read-only review has a labelled top
  // section. Nested LayoutGridField uses (e.g. for nested objects) skip this.
  // The edit page renders one section at a time under its own numbered header,
  // so it sets `suppressRootSectionHeading` to avoid duplicating that label.
  const suppressRootSectionHeading = (
    registry?.formContext as { suppressRootSectionHeading?: boolean } | undefined
  )?.suppressRootSectionHeading;
  const isRoot =
    (!fieldPathId || fieldPathId.$id === "root") && !suppressRootSectionHeading;

  // Only show the heading when there is at least one scalar (non-array/object)
  // field at root to group under it. Narrative-only reports render a schema whose
  // sole property is the `Narratives` array, so the heading is omitted there.
  const hasScalarRootField = Object.values(
    resolvedSchema.properties ?? {}
  ).some((propSchema) => {
    if (typeof propSchema === "boolean") return false;
    const type = (propSchema as RJSFSchema).type;
    const types = Array.isArray(type) ? type : [type];
    return !types.includes("array") && !types.includes("object");
  });

  return (
    <Grid container spacing={2}>
      {isRoot && hasScalarRootField && (
        <Grid size={{ xs: 12 }} data-section-key="GeneralInformation">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            General Information
          </Typography>
        </Grid>
      )}
      {layoutGrid["ui:row"].map(
        (rowWrapper: LayoutGridRow, rowIndex: number) => {
          const row = rowWrapper["ui:row"];
          if (!row) return null;

          return (
            <Grid
              container
              key={rowIndex}
              spacing={row.spacing || 2}
              className={row.className}
              size={{ xs: 12 }}
            >
              {row.children &&
                row.children.map(
                  (colWrapper: LayoutGridCol, colIndex: number) => {
                    const col = colWrapper["ui:col"];
                    if (!col) return null;

                    return (
                      <Grid
                        key={colIndex}
                        size={{
                          xs: col.size?.xs || 12,
                          sm: col.size?.sm,
                          md: col.size?.md,
                          lg: col.size?.lg,
                          xl: col.size?.xl,
                        }}
                      >
                        {col.children &&
                          col.children.map(
                            (fieldPath: string, fieldIndex: number) => {
                              // Support both simple field names and dot-notation paths
                              const isNestedPath = fieldPath.includes(".");

                              if (isNestedPath) {
                                // Handle nested path like "Person.FirstName"
                                if (
                                  !hasNestedSchemaProperty(
                                    resolvedSchema,
                                    fieldPath,
                                    registry.schemaUtils,
                                    formData
                                  )
                                ) {
                                  return null;
                                }

                                const fieldSchema = getNestedSchemaProperty(
                                  resolvedSchema,
                                  fieldPath,
                                  registry.schemaUtils,
                                  formData
                                );
                                if (!fieldSchema) return null;

                                const childFieldPathId =
                                  getChildFieldPathId(fieldPath);
                                const fieldUiSchema =
                                  getNestedValue<UiSchema>(
                                    uiSchema as Record<string, unknown>,
                                    fieldPath
                                  ) ?? {};
                                const fieldErrorSchema =
                                  getNestedValue<ErrorSchema<unknown>>(
                                    errorSchema as Record<string, unknown>,
                                    fieldPath
                                  ) ?? {};
                                const fieldFormData = getNestedValue<unknown>(
                                  formData as Record<string, unknown>,
                                  fieldPath
                                );

                                // For nested paths, use the last part as the display name
                                const pathParts = fieldPath.split(".");
                                const fieldName =
                                  pathParts[pathParts.length - 1];

                                return (
                                  <SchemaField
                                    key={fieldIndex}
                                    name={fieldName}
                                    required={false} // TODO: Check nested required
                                    schema={fieldSchema}
                                    uiSchema={fieldUiSchema}
                                    errorSchema={fieldErrorSchema}
                                    fieldPathId={childFieldPathId}
                                    formData={fieldFormData}
                                    onChange={handleChildChange(fieldPath)}
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                    registry={registry}
                                    disabled={disabled}
                                    readonly={readonly}
                                  />
                                );
                              } else {
                                // Handle simple field name (original behavior)
                                if (
                                  !resolvedSchema.properties ||
                                  !resolvedSchema.properties[fieldPath]
                                ) {
                                  return null;
                                }

                                const childFieldPathId =
                                  getChildFieldPathId(fieldPath);

                                const required = resolvedSchema.required
                                  ? resolvedSchema.required.includes(fieldPath)
                                  : false;

                                // Get the field schema, handling both original and resolved
                                const rawFieldSchema =
                                  schema.properties?.[fieldPath] ??
                                  resolvedSchema.properties[fieldPath];
                                // Skip boolean schemas (which means "any type allowed")
                                if (typeof rawFieldSchema === "boolean") {
                                  return null;
                                }
                                const fieldSchema =
                                  rawFieldSchema as RJSFSchema;

                                return (
                                  <SchemaField
                                    key={fieldIndex}
                                    name={fieldPath}
                                    required={required}
                                    schema={fieldSchema}
                                    uiSchema={
                                      (uiSchema?.[fieldPath] as UiSchema) ?? {}
                                    }
                                    errorSchema={
                                      (errorSchema?.[
                                        fieldPath
                                      ] as ErrorSchema<unknown>) ?? {}
                                    }
                                    fieldPathId={childFieldPathId}
                                    formData={
                                      (formData as Record<string, unknown>)?.[
                                        fieldPath
                                      ]
                                    }
                                    onChange={handleChildChange(fieldPath)}
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                    registry={registry}
                                    disabled={disabled}
                                    readonly={readonly}
                                  />
                                );
                              }
                            }
                          )}
                      </Grid>
                    );
                  }
                )}
            </Grid>
          );
        }
      )}
    </Grid>
  );
};
